import {
	Building,
	Calendar,
	Factory,
	Globe2,
	Hash,
	MapPin,
	MoreVertical,
	PencilLine,
	Tag,
	Trash2,
	UserPlus,
} from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useCompanyStore } from '../hooks/useCompanyStore';
import { useTran } from '../../../hooks/useTran';
import { getLabel } from '../../../utils/utils';
import dayjs from 'dayjs';
import UserDetailIcon from '../../../assets/icons/UserDetailIcon';
import { COMPANY_TYPE_OPTIONS, INDUSTRIAL_CLASSIFICATION } from '../../OnBoard/constants/onboard.constant';
import { useUserStore } from '../../User/hooks/useUserStore';
import { COMPANY } from '../constants/companyText.constants';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../Auth';
import { SYSTEM_ADMIN } from '../../../constants/constants';
import { useCNPStore } from '../../CNP/hooks/useCNPStore';
import { COMMON } from '../../../constants/commonText.constants';

import { Button } from '@/components/ui/button';
import { Dropdown } from '@/components/ui/dropdown-menu';
const CompanyCard = observer(({ company, companyModulePermission, userModulePermission }) => {
	const { t } = useTran();
	const { setCompanyForm, openDeleteConfirmModal, setSelectedCompanyId, openCompanyModal } = useCompanyStore();
	const { openUserModal } = useUserStore();
	const { setSelectedAsset } = useCNPStore();
	const { _id, companyName, country, address, businessNo, companyType, industrySector, createdAt, isActive } =
		company || {};

	const { role } = useAuthStore();

	const navigate = useNavigate();

	const createdDate = dayjs(createdAt);
	const formattedDate = createdDate.isValid() ? createdDate.format('DD/MM/YYYY h:mmA') : '-';

	const _handleEdit = (id) => {
		setCompanyForm(id);
		openCompanyModal();
	};

	const _handleView = () => {
		setSelectedCompanyId(_id);
		setSelectedAsset();
		navigate(`/onboarding/0/${_id}`);
	};

	const { update, drop } = companyModulePermission;
	const isActiveCompany = isActive !== false;
	const statusLabel = isActiveCompany ? t('Active') : t('Inactive');
	const statusClassName = isActiveCompany
		? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300'
		: 'bg-muted text-muted-foreground';
	const countryLabel = country ? t(country) : '-';
	const displayValue = (value) => (value === null || value === undefined || value === '' ? '-' : value);
	const companyTypeLabel = displayValue(getLabel(COMPANY_TYPE_OPTIONS, companyType));
	const industryLabel = displayValue(getLabel(INDUSTRIAL_CLASSIFICATION, industrySector));
	const detailRows = [
		{ key: 'businessNo', icon: Hash, label: t('Business No.'), value: displayValue(businessNo) },
		{ key: 'createdAt', icon: Calendar, label: t('Created'), value: formattedDate },
		{ key: 'companyType', icon: Tag, label: t('Company Type'), value: companyTypeLabel },
		{ key: 'industry', icon: Factory, label: t('Industry'), value: industryLabel },
		{ key: 'address', icon: MapPin, label: t('Address'), value: displayValue(address) },
	];
	const actionItems = [
		update && {
			label: (
				<>
					<PencilLine className="h-4 w-4 text-muted-foreground" />
					<span>{t(COMPANY.EDIT_COMPANY)}</span>
				</>
			),
			onClick: () => _handleEdit(_id),
			dataCy: 'edit-company',
		},
		userModulePermission?.create && {
			label: (
				<>
					<UserPlus className="h-4 w-4 text-muted-foreground" />
					<span>{t('Assign users')}</span>
				</>
			),
			onClick: () => openUserModal(_id),
			dataCy: 'add-user',
		},
		role !== SYSTEM_ADMIN && {
			label: (
				<>
					<UserDetailIcon
						size={16}
						color="#6b7280"
					/>
					<span>{t(COMPANY.VIEW_AS)}</span>
				</>
			),
			onClick: _handleView,
		},
		drop && {
			label: (
				<>
					<Trash2 className="h-4 w-4" />
					<span>{t(COMPANY.DELETE_COMPANY)}</span>
				</>
			),
			onClick: () => openDeleteConfirmModal(_id, companyName),
			className: 'text-destructive',
		},
	].filter(Boolean);

	return (
		<div className="rounded-lg bg-card border border-border flex flex-col justify-between gap-y-4 p-4">
			<div className="space-y-3">
				<div className="flex items-start justify-between gap-3">
					<div className="flex items-start gap-3">
						<div className="flex items-center justify-center h-12 w-12 rounded-lg border border-border bg-emerald-50 dark:bg-emerald-500/15">
							<Building
								size={26}
								className="text-emerald-600 dark:text-emerald-300"
							/>
						</div>
						<div className="space-y-1">
							<div className="flex flex-wrap items-center gap-2">
								<p className="font-semibold text-foreground">{companyName}</p>
								<span
									className={`inline-flex items-center rounded-lg border border-border px-2 py-0.5 text-xs ${statusClassName}`}
								>
									{statusLabel}
								</span>
							</div>
							<div className="flex items-center gap-2 text-sm text-muted-foreground">
								<Globe2 className="h-4 w-4" />
								<span>{countryLabel}</span>
							</div>
						</div>
					</div>
					{actionItems.length > 0 && (
						<Dropdown
							align="right"
							trigger={
								<div
									className="flex h-9 w-9 items-center justify-center rounded-lg border border-border text-muted-foreground transition hover:bg-muted hover:text-foreground"
									data-cy="company-actions"
								>
									<MoreVertical className="h-4 w-4" />
								</div>
							}
							items={actionItems}
							menuClassName="rounded-lg shadow-none ring-0 border border-border"
							itemClassName="flex items-center gap-2"
						/>
					)}
				</div>
				<div className="grid gap-y-3">
					{detailRows.map(({ key, icon: Icon, label, value }) => (
						<div
							key={key}
							className="grid grid-cols-[18px_120px_1fr] items-start gap-2 text-sm"
						>
							<Icon className="h-4 w-4 text-muted-foreground" />
							<p className="text-muted-foreground">{label}</p>
							<p className="text-foreground break-words">{value}</p>
						</div>
					))}
				</div>
			</div>
			<div className="flex items-center justify-end gap-2">
				{role !== SYSTEM_ADMIN && (
					<Button
						size="sm"
						variant="outline"
						onClick={_handleView}
					>
						{t(COMPANY.VIEW_AS)}
					</Button>
				)}
				{drop && (
					<Button
						variant="outline"
						size="sm"
						className="text-destructive gap-x-2"
						onClick={() => openDeleteConfirmModal(_id, companyName)}
					>
						<Trash2
							size={16}
							className="text-destructive"
						/>
						{t(COMMON.DELETE)}
					</Button>
				)}
			</div>
		</div>
	);
});

export default CompanyCard;
